import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, ElementRef, inject, OnInit, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SPIN_APPEND, SPIN_STYLES, SpinInputModel, UserDetailModel } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { FullAddressModel } from 'src/app/model/common/full-address.model';
import { CountryModel } from 'src/app/model/country.model';
import { ApiService } from 'src/app/services/api.service';
import { AclModel } from '../../../model/acl.model';
import { AddressV2Model } from '../../../model/address-v2.model';
import { BillingModel } from '../../../model/billing/billing.model';
import { FirmUpdateModel } from '../../../model/firm/firm-update.model';
import { FirmModel } from '../../../model/firm/firm.model';
import { AclService } from '../../../services/acl.service';
import { AuthService } from '../../../services/auth.service';
import { BillingService } from '../../../services/billing.service';
import { FirmService } from '../../../services/firm.service';
import { alphaNumericRegex, ValidateLength } from '../../shared/common-validator';
import { CommonModule } from '@angular/common';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { SpinDirective } from '../../../directives/spin.directive';
import { DirectivesModule } from '../../../directives/directives.module';
import { CountableMatDateComponent } from '../../remote-shared/countable-mat-date/countable-mat-date.component';
@Component({
  selector: 'app-firm-information',
  templateUrl: './firm-information.component.html',
  styleUrls: ['./firm-information.component.scss'],
  standalone: true,
  imports:   [ 
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatDialogModule,
    MatAutocompleteModule,
    SpinDirective,
    DirectivesModule,
    CountableMatDateComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FirmInformationComponent implements OnInit {
  @ViewChild('firmName') firmNameFocus: ElementRef;
  @ViewChild('pegAccountLocked') pegAccountLocked: TemplateRef<any>;
  @ViewChild('updateFirmAddressDialog') updateFirmAddressDialog: TemplateRef<any>;

  userDetail: UserDetailModel;
  firmInfoForm: UntypedFormGroup;
  zipOrPostCode: string;
  provinceOrState: string;
  pegSubscriberDetails = [{'name': 'Yes', 'id': 1}, {'name': 'No', 'id': 2}];

  firm: FirmModel;
  firmAddress: AddressV2Model;
  billing: BillingModel;
  acl: AclModel;
  countries: CountryModel[] = [];
  private destroyRef = inject(DestroyRef);
  public autoCompleteSuggestions: WritableSignal<FullAddressModel[]> = signal<FullAddressModel[]>([]);
  public spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(new SpinInputModel(SPIN_APPEND.HOST, SPIN_STYLES.BLUR_WITH_SPIN, false));
  private spinCount: number = 0;
  initialFirmInfo: WritableSignal<any> = signal<any>({});
  formValueSignal: WritableSignal<any> = signal<any>({});
  isNoDataChanges = computed(() => {
    const formValue = this.formValueSignal();
    const originalValue = this.initialFirmInfo();
    const normalizedFormValue = { ...formValue };
    const normalizedOriginalValue = { ...originalValue };    
    Object.keys(normalizedFormValue).forEach(key => {
      if (normalizedFormValue[key] === null) delete normalizedFormValue[key];
      if (normalizedOriginalValue[key] === null) delete normalizedOriginalValue[key];
    });    
    return JSON.stringify(normalizedFormValue) === JSON.stringify(normalizedOriginalValue);
  });

  constructor(private toastr: ToastrService, public dialog: MatDialog, private cdr: ChangeDetectorRef,
              private firmService: FirmService, private billingService: BillingService, private authService: AuthService,
              private aclService: AclService, private apiService: ApiService) {
  }

  ngOnInit() {
    this.authService.refreshUserProfile().subscribe();
    this.firmService.refresh();
    this.firmInfoForm = new UntypedFormGroup({
      firmname: new UntypedFormControl('', [Validators.required, ValidateLength]),
      displayName: new UntypedFormControl(''),
      address: new UntypedFormControl('', Validators.required),
      city: new UntypedFormControl('', Validators.required),
      state: new UntypedFormControl('', Validators.required),
      country: new UntypedFormControl('', Validators.required),
      postalcode: new UntypedFormControl('', Validators.required),
      pegsubscriber: new UntypedFormControl(''),
      pegLicenseNo: new UntypedFormControl(''),
      pegExpiryDate: new UntypedFormControl('')
    });
    // login user
    this.authService.subjectUserDetail.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(detail => {
      this.userDetail = detail;
    });
    // firm & billing
    this.firmService.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(firm => {
      if (firm) {
        this.firm = firm;
        this.firmService.getAddress().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(address => {
          this.firmAddress = address;
          this.bindFirmInformation();
          this.showEditFirmForm();
        });
      }
    });
    this.billingService.getBillingDetail().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(billing => {
      if (!billing) {
        return;
      }
      this.billing = billing;
    });
    this.aclService.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res) {
        this.acl = res;
      }
    });
    this.apiService.getCountries(1).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res) {
        this.countries = res;
      }
    });
    this.firmInfoForm.valueChanges.subscribe(() => {
      this.formValueSignal.set({
        ...this.firmInfoForm.getRawValue(),
        pegExpiryDate: this.firmInfoForm.value.pegExpiryDate
          ? this.firmInfoForm.value.pegExpiryDate.split('T')[0]
          : null
      });
    });
  }

  bindFirmInformation() {
    this.firmInfoForm.patchValue({
      firmname: this.firm.name,
      displayName: this.firm.displayName ? this.firm.displayName : this.firm.name,
      address: this.firmAddress.line1,
      city: this.firmAddress.city,
      state: this.firmAddress.state,
      country: this.firmAddress.country,
      postalcode: this.firmAddress.postalCode,
      pegsubscriber: this.firm.isPegPacSubscribed ? 'Yes' : 'No',
      pegLicenseNo: this.firm.pegPacInvoice,
      pegExpiryDate: this.firm.pegPacExpiryDate
    });
    this.initialFirmInfo.set(this.firmInfoForm.getRawValue());
  }

  updateFirmInformation() {
    if (!this.acl.hasFirmInformationEdit || !this.firmInfoForm.valid) {
      this.toastr.warning('Please fill the firm form correctly');
      return;
    }
    const firmData = this.firmInfoForm.value;
    let expiryDate: string = null;
    if (firmData.pegsubscriber === 'No') {
      firmData.pegLicenseNo = '';
    } else if (firmData.pegExpiryDate) {
      expiryDate = firmData.pegExpiryDate;
    }
    const data: FirmUpdateModel = {
      'name': firmData.firmname.trim(),
      'displayName': firmData?.displayName?.trim(),
      'address': firmData.address,
      'city': firmData.city,
      'state': firmData.state,
      'country': firmData.country,
      'zipcode': firmData.postalcode,
      'isPegPactSubscriber': firmData.pegsubscriber && firmData.pegsubscriber === 'Yes',
      'pegPacLicense': firmData.pegLicenseNo,
      'pegPactExpiry': expiryDate
    };
    this.updateSpinnerConfig(true);
    this.firmService.updateFirm(data).pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe({
      next: res => {
        if (res && res.status == 200) {
          this.userDetail['firmcity'] = firmData.city;
          this.authService.refreshUserProfile().subscribe();
          this.firmService.refresh();
          this.toastr.success('Firm information updated successfully');
          this.initialFirmInfo.set(this.firmInfoForm.getRawValue());
        } else {
          res.errors.forEach(error => {
            this.toastr.error(error);
          });
        }
      },
      error: err => {
        this.toastr.error(err.error);
      }
    });
  }

  alphaNumeric(event, type: string) {
    const alphaNumeric = event.target.value.replace(alphaNumericRegex, '');
    if (type == 'pegLicenseNo') {
      this.firmInfoForm.controls[type].setValue(alphaNumeric);
    }
    if (type == 'firmname') {
      this.firmInfoForm.controls[type].setValue(alphaNumeric);
    }
    if (type == 'displayName') {
      this.firmInfoForm.controls[type].setValue(alphaNumeric);
    }

  }

  checkDates(vaule) {
    this.firmInfoForm.controls['pegExpiryDate'].setValue(vaule);
    if (this.firmInfoForm.controls['pegExpiryDate'].invalid) {
      this.toastr.error('Please enter valid date in MM/DD/YYYY format');
    }
  }

  showEditFirmForm() {
    setTimeout(() => {
      this.firmNameFocus.nativeElement.focus();
    }, 100);
    if (this.firm.isPegPacMandatory) {
      this.firmInfoForm.get('pegLicenseNo').setValidators([Validators.required]);
      this.firmInfoForm.get('pegExpiryDate').setValidators([Validators.required]);
    } else {
      this.firmInfoForm.get('pegLicenseNo').setValidators(null);
      this.firmInfoForm.get('pegExpiryDate').setValidators(null);
    }
    if (this.firm.countryCode == 'CA') {
      this.firmInfoForm.get('postalcode').addValidators([Validators.pattern('^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] [0-9][A-Z][0-9]$'), Validators.minLength(7), Validators.maxLength(7)]);
    } else {
      this.firmInfoForm.get('postalcode').addValidators([Validators.pattern('^[0-9]{5}(?:-[0-9]{4})?$'), Validators.minLength(5), Validators.maxLength(10)]);
    }
    this.firmInfoForm.get('postalcode').updateValueAndValidity();
    this.firmInfoForm.get('pegLicenseNo').updateValueAndValidity();
    this.firmInfoForm.markAllAsTouched();
    this.onSelectionChange();
  }

  onSelectionChange() {
    if (this.firmInfoForm.value.pegsubscriber === 'Yes') {
      this.firmInfoForm.get('pegLicenseNo').setValidators([Validators.required]);
      this.firmInfoForm.get('pegExpiryDate').setValidators([Validators.required]);
    } else {
      this.firmInfoForm.get('pegLicenseNo').setValue(null);
      this.firmInfoForm.get('pegLicenseNo').setValidators(null);
      this.firmInfoForm.get('pegExpiryDate').setValue(null);
      this.firmInfoForm.get('pegExpiryDate').setValidators(null);
    }
    this.firmInfoForm.get('pegLicenseNo').updateValueAndValidity();
    this.firmInfoForm.get('pegExpiryDate').updateValueAndValidity();
  }



  updateFirmAddressInfo() {
    if ((this.firmAddress.state.toLowerCase() == this.firmInfoForm.controls['state'].value.toLowerCase()) && (this.firmAddress.country.toLowerCase() == this.firmInfoForm.controls['country'].value.toLowerCase()) && (this.firmAddress.postalCode.toLowerCase() == this.firmInfoForm.controls['postalcode'].value.toLowerCase())) {
      this.updateFirmInformation();
    } else {
      this.dialog.open(this.updateFirmAddressDialog);
    }
  }

  public onAddressType(address: FullAddressModel[]) {
    this.autoCompleteSuggestions.set(address);
  }

  public selectedSuggestion(matOption: MatAutocompleteSelectedEvent) {
    this.firmInfoForm.controls['address'].patchValue(matOption.option.value.address);
    this.firmInfoForm.controls['postalcode'].patchValue(matOption.option.value.postalCode);
    this.firmInfoForm.controls['city'].patchValue(matOption.option.value.city);
    this.firmInfoForm.controls['state'].patchValue(matOption.option.value.jurisdictionCode);
    this.firmInfoForm.controls['country'].patchValue(matOption.option.value.country);
    if (matOption.option.value.countryCode == 'CA') {
      this.zipOrPostCode = 'Postal Code';
      this.provinceOrState = 'Province';
    } else {
      this.zipOrPostCode = 'Zip Code';
      this.provinceOrState = 'State';
    }
  }

  private updateSpinnerConfig(value: boolean) {
    value ? this.spinCount++ : (this.spinCount = Math.max(0, this.spinCount - 1));
    this.spinnerConfig.update((config) => ({ ...config, isSpinShown: this.spinCount > 0 }));
  }
}
